import styled, { css } from 'styled-components';

export const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

export const GearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid #d7d7d7;
  background: #fff;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease-in;

  img {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #f0f0f0;
  }

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      border-color: #464b51;
      background: #4f5761;

      img {
        filter: invert(1);
      }

      &:hover {
        background: #5a6270;
      }
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      width: 38px;
      height: 38px;
      border-radius: 8px;
      border: 1px solid var(--cp-border);
      background: var(--cp-surface);
      transition: border-color 0.15s ease, background 0.15s ease;
      img {
        filter: ${props.theme.theme === 'dark' ? 'invert(1)' : 'none'};
      }
      &:hover {
        background: var(--cp-surface);
        border-color: var(--cp-green);
      }
    `}
`;

export const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 280px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #d7d7d7;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 12px;
  z-index: 100;
  text-align: left;

  ${(props) =>
    props.$flip &&
    css`
      top: auto;
      bottom: calc(100% + 6px);
    `}

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      background: #464b51;
      border-color: #5a6068;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      width: 288px;
      /* cap at the viewport height; the list inside scrolls */
      max-height: calc(100vh - 100px);
      background: var(--cp-surface);
      border: 1px solid var(--cp-border);
      border-radius: 12px;
      box-shadow: 0 14px 40px rgba(0, 0, 0, 0.22);
      padding: 10px;
    `}
`;

export const Title = styled.div`
  flex: none;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      color: #eee;
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      color: var(--cp-text);
      padding: 4px 6px 8px;
      margin-bottom: 4px;
      border-bottom: 1px solid var(--cp-border-soft);
    `}
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  max-height: 300px;

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      &::-webkit-scrollbar {
        width: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: var(--cp-scroll-thumb);
        border-radius: 4px;
      }
    `}
`;

export const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px;
  border-radius: 4px;
  background: #fff;
  position: relative;

  ${(props) =>
    props.$dragging &&
    css`
      background: #f5f5f5;
      z-index: 1;
    `}

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    ${(props) =>
      props.$forced &&
      css`
        opacity: 0.55;
      `}
  }

  input[type='checkbox'] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    flex: none;
  }

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      background: transparent;

      ${props.$dragging &&
      css`
        background: #575e66;
      `}

      label {
        color: #ddd;
      }
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      background: transparent;
      border-radius: 8px;
      padding: 7px 6px;
      &:hover {
        background: var(--cp-surface-hover);
      }
      ${props.$dragging &&
      css`
        background: var(--cp-surface-hover);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
      `}
      label {
        color: var(--cp-text);
      }
      input[type='checkbox'] {
        accent-color: var(--cp-green);
      }
    `}
`;

export const DragHandle = styled.div`
  cursor: grab;
  color: #aaa;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      color: #9aa0a6;
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      color: var(--cp-text-tertiary);
    `}
`;

export const Footer = styled.div`
  flex: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      border-top-color: #5a6068;
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      border-top-color: var(--cp-border-soft);
      padding: 10px 6px 4px;
    `}
`;

export const ResetLink = styled.button`
  background: none;
  border: none;
  color: #6a6a6a;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #000;
  }

  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      color: #b0b6bd;

      &:hover {
        color: #fff;
      }
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      color: var(--cp-text-secondary);
      text-decoration: none;
      &:hover {
        color: var(--cp-green);
      }
    `}
`;
